import React from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/cjs/styles/prism";
import type { TranscriptMessage } from "./livekit-types";

/** Memoized message component to prevent rerendering flash */
const TranscriptMessageComponent = React.memo<{
    message: TranscriptMessage;
    darkMode?: boolean;
}>(({ message, darkMode }) => {
    return (
        <div
            className={`transcript-message ${message.sender} ${darkMode ? "dark" : ""} ${message.isFinal === false ? "interim" : ""}`}
        >
            <div className="message-header">
                <span className="message-sender">
                    {message.sender === "agent" ? "Alex" : "You"}
                </span>
                {message.isFinal === false && (
                    <span className="interim-badge" title="Transcribing...">●</span>
                )}
            </div>
            <div className="message-text transcript-markdown">
                <ReactMarkdown
                    components={{
                        code({ node, className, children, ref, ...props }) {
                            const match = /language-(\w+)/.exec(className || "");
                            const content = String(children).replace(/\n$/, "");
                            const isInline = !match && !content.includes("\n");

                            if (isInline) {
                                return (
                                    <code
                                        className={className}
                                        {...props}
                                        style={{
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontSize: "0.9em",
                                            fontFamily: '"Fira Code", monospace',
                                        }}
                                    >
                                        {children}
                                    </code>
                                );
                            }

                            return (
                                <SyntaxHighlighter
                                    style={dracula as any}
                                    language={match ? match[1] : "text"}
                                    PreTag="div"
                                    customStyle={{
                                        margin: "0.5em 0",
                                        borderRadius: "6px",
                                        fontSize: "0.85em",
                                        padding: "0.8em",
                                    }}
                                >
                                    {content}
                                </SyntaxHighlighter>
                            );
                        },
                    }}
                >
                    {message.text}
                </ReactMarkdown>
            </div>
            <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Only re-render if text or isFinal status changes
    return prevProps.message.text === nextProps.message.text &&
        prevProps.message.isFinal === nextProps.message.isFinal;
});

export default TranscriptMessageComponent;
